import {ChangeDetectorRef, Component, computed, InputSignal, OnDestroy, OnInit, OutputEmitterRef, Signal, effect, EffectRef, inject, input, output} from '@angular/core';
import {DropDownListModule} from "@syncfusion/ej2-angular-dropdowns";
import {FormInputComponent} from "../form-input/form-input.component";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {FormLabelComponent} from "../form-label/form-label.component";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {TYPE} from "../../types/types";
import {finalize, forkJoin, of, Subscription} from "rxjs";
import {ApiService} from "../../services/api.service";
import {LoadingService} from "../../services/loading.service";
import {FormDropdownComponent} from "../form-dropdown/form-dropdown.component";
import {LanguageService} from "../../services/language.service";
import {IconBadgeComponent} from "../icon-badge/icon-badge.component";
import {LinkComponent} from "../link/link.component";
import {FormCheckboxComponent} from "../form-checkbox/form-checkbox.component";
import {Dialog, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {FormButtonComponent} from "../form-button/form-button.component";
import {TooltipComponent} from "../tooltip/tooltip.component";
import {HeadlineComponent} from "../headline/headline.component";
import {ContentComponent} from "../content/content.component";
import {AvatarComponent} from "../avatar/avatar.component";
import {GroupMinimalResource} from '../../types/generated/groupMinimalResource';
import {UserMinimalResource} from '../../types/generated/userMinimalResource';
import {CustomerActionType} from '../../types/generated/customerActionType';

type ApiGroupUsage = 'knowledgebase-page-editors' | 'knowledgebase-page-viewers' | 'feedback-form-editors' | '';
type ApiUserUsage = 'knowledgebase-page-editors' | 'feedback-form-editors' | '';

@Component({
    selector: 'app-form-user-group-mail',
    imports: [
        DropDownListModule,
        FormInputComponent,
        TranslatePipe,
        FormLabelComponent,
        ReactiveFormsModule,
        FormDropdownComponent,
        IconBadgeComponent,
        LinkComponent,
        FormCheckboxComponent,
        TooltipModule,
        FormButtonComponent,
        TooltipComponent,
        HeadlineComponent,
        ContentComponent,
        AvatarComponent
    ],
    templateUrl: './form-user-group-mail.component.html',
    styleUrl: './form-user-group-mail.component.scss'
})
export class FormUserGroupMailComponent implements OnInit, OnDestroy {
  private readonly pageSize: number = 25;
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private loadingService: LoadingService = inject(LoadingService);
  private languageService: LanguageService = inject(LanguageService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);


  public user: InputSignal<number[]> = input<number[]>([]);
  public groups: InputSignal<number[]> = input<number[]>([]);
  public adminUserIDs: InputSignal<number[]> = input<number[]>([]);
  public type: InputSignal<'own' | 'all' | 'active' | 'inactive' | 'registration'> = input<'own' | 'all' | 'active' | 'inactive' | 'registration'>('own'); // TODO: too many types, should be simplified
  public method: InputSignal<'user' | 'group' | 'userGroup'> = input<'user' | 'group' | 'userGroup'>('userGroup');
  public allowEmails: InputSignal<boolean> = input(false);
  public allowPush: InputSignal<boolean> = input(false);
  public pushUserIDs: InputSignal<number[]> = input<number[]>([]);
  public pushGroupIDs: InputSignal<number[]> = input<number[]>([]);
  public mails: InputSignal<string[]> = input<string[]>([]);
  public allowAdmins: InputSignal<boolean> = input(false);
  public isLimitedByAllowedIDs: InputSignal<boolean> = input(false);
  public allowedUserIDs: InputSignal<number[]> = input<number[]>([]);
  public allowedGroupIDs: InputSignal<number[]> = input<number[]>([]);
  public excludedUserIDs: InputSignal<number[]> = input<number[]>([]);
  public excludedGroupIDs: InputSignal<number[]> = input<number[]>([]);
  public usage: InputSignal<ApiGroupUsage> = input<ApiGroupUsage>('');
  public contextParentID: InputSignal<number | null> = input<number | null>(null);
  public readonly: InputSignal<boolean> = input(false);
  public externalEmailsOnly: InputSignal<boolean> = input(false);
  public showSelectedParticipants: InputSignal<boolean> = input(true);
  public allowGroupsToResolve: InputSignal<boolean> = input(false);
  public hasError: InputSignal<boolean> = input(false);
  public hasErrorField: InputSignal<boolean> = input(true);
  public singleSelection: InputSignal<boolean> = input(false);
  public singleSelectionStays: InputSignal<boolean> = input(false);
  public can: InputSignal<string> = input.required<string>();
  private validatedCan: Signal<CustomerActionType | ''> = computed(() => this.toCustomerActionType(this.can()));
  public userChange: OutputEmitterRef<number[]> = output<number[]>();
  public groupsChange: OutputEmitterRef<number[]> = output<number[]>();
  public mailsChange: OutputEmitterRef<string[]> = output<string[]>();
  public pushUserIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public pushGroupIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public adminUserIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public adminGroupIDsChange: OutputEmitterRef<number[]> = output<number[]>();
  public userNamesChange: OutputEmitterRef<string[]> = output<string[]>();
  public groupNamesChange: OutputEmitterRef<string[]> = output<string[]>();
  public userModelChange: OutputEmitterRef<UserMinimalResource[]> = output<UserMinimalResource[]>();
  public groupModelChange: OutputEmitterRef<GroupMinimalResource[]> = output<GroupMinimalResource[]>();
  public defaultPushState: InputSignal<boolean> = input(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public userGroupOptions: any[] = [];
  public userGroupMailForm: FormGroup = this.formBuilder.group({
    userGroup: [''],
    addMail: ['', [Validators.email]],
    userGroupMailFilter: ['all']
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public userGroupMailFilter: any[] = [];
  public participants: (TYPE.FieldSettingsModelWithId)[] = [];
  private allAllowedOptions: TYPE.FieldSettingsModelWithId[] = [];
  private subscriptions: Subscription[] = [];
  private page: number = 0;
  private searchString: string = '';
  private currentLoadingType: 'user' | 'group' = 'group';
  private dialogInstance: Dialog | null = null;
  private loadForbidden: boolean = false;
  private latestLimitedRequestId: number = 0; //identifies current search generation
  private limitedRequestInProgress: boolean = false; //blocks duplicated scroll requests
  private limitedInFlightToken: number = 0; //prevents an old requestâ€™s finalize() from altering the state of a newer request
  private latestStandardRequestId: number = 0;
  private standardRequestInProgress: boolean = false;
  private standardInFlightToken: number = 0;
  private initialsLoadVersion: number = 0;

  public constructor() {
    effect(() => {
      if (!this.allowPush()) {
        return;
      }

      let changed = false;
      this.participants.forEach((participant) => {
        if (participant.type === 'mail' || participant.push !== null && participant.push !== undefined) {
          return;
        }

        participant.push = false;
        changed = true;
      });

      if (changed) {
        this.emitPushChanges();
        this.cdr.markForCheck();
      }
    });
  }

  private inputsChangeEffect: EffectRef = effect(() => {
    this.user();
    this.groups();
    this.mails();
    this.adminUserIDs();
    this.pushUserIDs();
    this.pushGroupIDs();

    if (!this.hasDifferentParticipantsFromInputs()) {
      return;
    }

    this.loadInitials();
  });

  private excludedOptionsEffect: EffectRef = effect(() => {
    this.excludedUserIDs();
    this.excludedGroupIDs();

    this.removeExistingParticipants();
    this.cdr.markForCheck();
  });

  public ngOnInit(): void {
    if (this.method() === 'user') {
      this.currentLoadingType = 'user';
    }

    if (this.allowEmails()) {
      this.userGroupMailFilter = [
        {id: 'all', name: this.languageService.getTranslationByKey('SHOWALL')},
        {id: 'mail', name: this.languageService.getTranslationByKey('SHOWMAILONLY')},
        {id: 'group', name: this.languageService.getTranslationByKey('SHOWGROUPONLY')},
        {id: 'user', name: this.languageService.getTranslationByKey('SHOWUSERONLY')}
      ];
    } else {
      this.userGroupMailFilter = [
        {id: 'all', name: this.languageService.getTranslationByKey('SHOWALL')},
        {id: 'group', name: this.languageService.getTranslationByKey('SHOWGROUPONLY')},
        {id: 'user', name: this.languageService.getTranslationByKey('SHOWUSERONLY')}
      ];
    }

    this.load();
    this.loadInitials();
  }

  public canEditUserGroupSelection(): boolean {
    return !this.readonly() && !this.externalEmailsOnly();
  }

  public canEditExternalEmails(): boolean {
    return !this.readonly() || this.externalEmailsOnly();
  }

  public canEditParticipant(participant: TYPE.FieldSettingsModelWithId): boolean {
    if (!this.readonly()) {
      return true;
    }

    return this.externalEmailsOnly() && participant.type === 'mail';
  }

  public hasEditableParticipants(): boolean {
    return this.participants.some((participant) => this.canEditParticipant(participant));
  }

  public ngOnDestroy(): void {
    this.invalidateLimitedRequest(); //Defensively invalidate generation
    this.invalidateStandardRequest();
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private load(): void {
    if (this.loadForbidden) {
      return;
    }

    if (this.singleSelectionStays()) {
      this.ensureSingleSelectionPlaceholder();
    }

    if (this.usesLegacyIdLimitedMode()) {
      this.loadLimitedOptions();
      return;
    }

    if (this.currentLoadingType === 'group') {
      this.loadGroups();
    } else if (this.method() === 'user' || this.method() === 'userGroup') {
      this.loadUser();
    }
  }

  private loadLimitedOptions(): void {
    if (this.currentLoadingType === 'group' && this.method() !== 'user') {
      if (!this.allowedGroupIDs().length) {
        if (this.method() === 'group') {
          this.loadForbidden = true;
          return;
        }

        this.currentLoadingType = 'user';
        this.page = 0;
        this.load();
        return;
      }

      this.loadLimitedGroups();
      return;
    }

    if (this.method() === 'group') {
      this.loadForbidden = true;
      return;
    }

    if (!this.allowedUserIDs().length) {
      this.loadForbidden = true;
      return;
    }

    this.loadLimitedUsers();
  }

  private loadGroups(): void {
    const inFlightToken = this.beginStandardRequest();

    if (inFlightToken === null) {
      return;
    }

    const requestId = this.latestStandardRequestId;

    const subscription = this.apiService.getGroups(this.page, this.searchString, true, this.type(), this.validatedCan(), this.pageSize, [], this.getGroupApiUsage(), this.getApiContextParentID()).pipe(
      finalize(() => {
        this.finishStandardRequest(inFlightToken);
      })
    ).subscribe({
      next: (groupListItems: GroupMinimalResource[]) => {
        if (requestId !== this.latestStandardRequestId) {
          return;
        }

        const groupListItemsTmp = groupListItems.map(group => ({
          id: group.id,
          description: group.description,
          count: group.user_count,
          image: group.avatar?.path ?? '',
          name: group.name,
          type: 'group'
        }));

        this.userGroupOptions = [
          ...this.userGroupOptions,
          ...groupListItemsTmp
        ];

        if (groupListItems.length < this.pageSize) {
          if (this.method() === 'group') {
            this.loadForbidden = true;
          } else {
            this.currentLoadingType = 'user';
            this.page = 0;

            queueMicrotask(() => {
              if (requestId === this.latestStandardRequestId) {
                this.load();
              }
            });
          }
        }

        this.removeExistingParticipants();
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestId !== this.latestStandardRequestId) {
          return;
        }

        this.loadForbidden = true;
      }
    });

    this.subscriptions.push(subscription);
  }

  private loadUser(): void {
    const inFlightToken = this.beginStandardRequest();

    if (inFlightToken === null) {
      return;
    }

    const requestId = this.latestStandardRequestId;

    const subscription = this.apiService.getUsers(this.page, this.searchString, this.type(), this.validatedCan(), this.pageSize, [], this.getUserApiUsage(), this.getApiContextParentID()).pipe(
      finalize(() => {
        this.finishStandardRequest(inFlightToken);
      })
    ).subscribe({
      next: (userListItems: UserMinimalResource[]) => {
        if (requestId !== this.latestStandardRequestId) {
          return;
        }

        const userListItemsTmp = userListItems.map(user => ({
          id: user.id,
          image: user.avatar?.path ?? '',
          name: user.last_name + ', ' + user.first_name,
          type: 'user'
        }));

        this.userGroupOptions = [...this.userGroupOptions, ...userListItemsTmp];

        if (!userListItemsTmp.length) {
          this.loadForbidden = true;
        }

        this.removeExistingParticipants();
        this.cdr.markForCheck();
      },
      error: () => {
        if (requestId !== this.latestStandardRequestId) {
          return;
        }

        this.loadForbidden = true;
      }
    });

    this.subscriptions.push(subscription);
  }
  private getApiContextParentID(): number | null {
    const contextParentID = this.contextParentID();

    if (contextParentID === null || contextParentID <= 0) {
      return null;
    }

    return this.validatedCan() === CustomerActionType.KNOWLEDGEBASE || this.validatedCan() === CustomerActionType.FEEDBACK
      ? contextParentID
      : null;
  }

  private getGroupApiUsage(): ApiGroupUsage {
    return this.getApiUsage();
  }

  private getUserApiUsage(): ApiUserUsage {
    const usage = this.getApiUsage();

    return usage === 'knowledgebase-page-viewers' ? '' : usage;
  }

  private getApiUsage(): ApiGroupUsage {
    const usage = this.usage();

    if (this.validatedCan() === CustomerActionType.KNOWLEDGEBASE) {
      if (this.getApiContextParentID() === null) {
        return '';
      }

      return usage === 'feedback-form-editors' ? '' : usage;
    }

    if (this.validatedCan() === CustomerActionType.FEEDBACK) {
      return usage === 'feedback-form-editors' ? usage : '';
    }

    return '';
  }
  private usesLegacyIdLimitedMode(): boolean {
    return this.isLimitedByAllowedIDs() && this.validatedCan() !== CustomerActionType.KNOWLEDGEBASE;
  }

  private toCustomerActionType(value: string): CustomerActionType | '' {
    if (Object.values(CustomerActionType).includes(value as CustomerActionType)) {
      return value as CustomerActionType;
    }

    return '';
  }

  private beginStandardRequest(): number | null {
    if (this.standardRequestInProgress) {
      return null;
    }

    this.standardRequestInProgress = true;
    return ++this.standardInFlightToken;
  }

  private finishStandardRequest(token: number): void {
    if (token !== this.standardInFlightToken) {
      return;
    }

    this.standardRequestInProgress = false;
  }

  private invalidateStandardRequest(): void {
    this.latestStandardRequestId++;
    this.standardInFlightToken++;
    this.standardRequestInProgress = false;
  }

  private beginLimitedRequest(): number | null {
    if (this.limitedRequestInProgress) {
      return null;
    }

    this.limitedRequestInProgress = true;
    return ++this.limitedInFlightToken;
  }

  private finishLimitedRequest(token: number): void {
    if (token !== this.limitedInFlightToken) {
      return;
    }

    this.limitedRequestInProgress = false;
  }

  private invalidateLimitedRequest(): void {
    this.latestLimitedRequestId++;
    this.limitedInFlightToken++;
    this.limitedRequestInProgress = false;
  }

  private loadLimitedGroups(): void {
    const inFlightToken = this.beginLimitedRequest();

    if (inFlightToken === null) {
      return;
    }

    const requestId = this.latestLimitedRequestId;

    const subscription = this.apiService.getGroups(
      this.page,
      this.searchString,
      true,
      this.type(),
      this.validatedCan(),
      this.pageSize,
      this.allowedGroupIDs()
    ).pipe(
      finalize(() => {
        this.finishLimitedRequest(inFlightToken);
      })
    ).subscribe({
      next: (groupListItems: GroupMinimalResource[]) => {
        if (requestId !== this.latestLimitedRequestId) {
          return;
        }

        const allowedGroupIDsSet = new Set(this.allowedGroupIDs());

        const groupListItemsTmp: TYPE.FieldSettingsModelWithId[] =
          groupListItems
            .filter((group) => allowedGroupIDsSet.has(group.id))
            .map((group) => ({
              id: group.id,
              description: group.description,
              count: group.user_count,
              image: group.avatar?.path ?? '',
              name: group.name,
              type: 'group'
            }));

        this.appendLimitedOptions(groupListItemsTmp);
        this.refreshAvailableOptions();

        // Determine exhaustion from the raw backend page,
        // not from the defensively filtered result.
        if (groupListItems.length < this.pageSize) {
          if (this.method() === 'group') {
            this.loadForbidden = true;
            return;
          }

          this.currentLoadingType = 'user';
          this.page = 0;

          /*
          * Do not call load() synchronously here because the current request's
          * finalize() has not yet released limitedRequestInProgress.
          */
          queueMicrotask(() => {
            if (requestId === this.latestLimitedRequestId) {
              this.load();
            }
          });
        }
      },
      error: () => {
        if (requestId !== this.latestLimitedRequestId) {
          return;
        }

        // An HTTP error does not mean that the group collection is exhausted.
        this.loadForbidden = true;
      }
    });

    this.subscriptions.push(subscription);
  }

  private loadLimitedUsers(): void {
    const inFlightToken = this.beginLimitedRequest();

    if (inFlightToken === null) {
      return;
    }

    const requestId = this.latestLimitedRequestId;

    const subscription = this.apiService.getUsers(
      this.page,
      this.searchString,
      this.type(),
      this.validatedCan(),
      this.pageSize,
      this.allowedUserIDs()
    ).pipe(
      finalize(() => {
        this.finishLimitedRequest(inFlightToken);
      })
    ).subscribe({
      next: (userListItems: UserMinimalResource[]) => {
        if (requestId !== this.latestLimitedRequestId) {
          return;
        }

        const allowedUserIDsSet = new Set(this.allowedUserIDs());

        const userListItemsTmp: TYPE.FieldSettingsModelWithId[] =
          userListItems
            .filter((user) => allowedUserIDsSet.has(user.id))
            .map((user) => ({
              id: user.id,
              image: user.avatar?.path ?? '',
              name: `${user.last_name}, ${user.first_name}`,
              type: 'user'
            }));

        this.appendLimitedOptions(userListItemsTmp);
        this.refreshAvailableOptions();

        // Use the raw backend response to determine exhaustion.
        if (userListItems.length < this.pageSize) {
          this.loadForbidden = true;
        }
      },
      error: () => {
        if (requestId !== this.latestLimitedRequestId) {
          return;
        }

        this.loadForbidden = true;
      }
    });

    this.subscriptions.push(subscription);
  }

  private appendLimitedOptions(options: TYPE.FieldSettingsModelWithId[]): void {
    if (!options.length) {
      return;
    }

    const optionMap = new Map<string, TYPE.FieldSettingsModelWithId>();
    [...this.allAllowedOptions, ...options].forEach((option) => {
      optionMap.set(option.type + ':' + option.id, option);
    });

    this.allAllowedOptions = [...optionMap.values()];
  }

  public loadNextPage(): void {
    if (this.loadForbidden) {
      return;
    }

    if (this.usesLegacyIdLimitedMode()) {
      if (this.limitedRequestInProgress) {
        return;
      }

      this.page++;
      this.load();
      return;
    }

    if (this.standardRequestInProgress) {
      return;
    }

    if (!this.loadingService.getLoadingIndicator()) {
      this.page++;
      this.load();
    }
  }

  public search(searchString: string): void {
    if (searchString === this.searchString) {
      return;
    }

    if (!this.loadingService.getLoadingIndicator()) {
      if (this.usesLegacyIdLimitedMode()) {
        this.invalidateLimitedRequest();
        this.searchString = searchString;
        this.page = 0;
        this.allAllowedOptions = [];
        this.userGroupOptions = [];
        this.loadForbidden = false;
        this.currentLoadingType = this.method() === 'user' ? 'user' : 'group';
        this.load();
        return;
      }

      this.invalidateStandardRequest();

      if (this.method() !== 'user') {
        this.currentLoadingType = 'group';
      }
      this.searchString = searchString;
      this.page = 0;
      this.userGroupOptions = [];
      this.loadForbidden = false;
      this.load();
    }
  }
  
  public resetSearchOnClose(): void {
    this.invalidateStandardRequest();
    this.invalidateLimitedRequest();

    if (!this.searchString) {
      return;
    }

    this.searchString = '';
    this.page = 0;
    this.loadForbidden = false;
    this.currentLoadingType = this.method() === 'user'
      ? 'user'
      : 'group';

    this.userGroupOptions = [];

    if (this.usesLegacyIdLimitedMode()) {
      this.allAllowedOptions = [];
    }

    this.load();
  }

  private refreshAvailableOptions(): void {
    this.userGroupOptions = [...this.allAllowedOptions];
    this.ensureSingleSelectionPlaceholder();
    this.removeExistingParticipants();
    this.cdr.markForCheck();
  }
  private ensureSingleSelectionPlaceholder(): void {
    if (!this.singleSelectionStays()) {
      return;
    }

    const hasPlaceholder = this.userGroupOptions.some((item) => item.id === 0 && item.name === '' && !item.type);
    if (!hasPlaceholder) {
      // Create new array reference instead of mutating
      this.userGroupOptions = [{
        id: 0,
        description: '',
        count: 0,
        image: '',
        name: ''
      }, ...this.userGroupOptions];
    }
  }

  private removeExistingParticipants(): void {
    const excludedUserIDs = new Set(this.excludedUserIDs().map((id) => Number(id)));
    const excludedGroupIDs = new Set(this.excludedGroupIDs().map((id) => Number(id)));

    // Create new array reference for change detection
    this.userGroupOptions = [...this.userGroupOptions.filter((item) => {
      const itemId = Number(item.id);
      const isExcludedUser = item.type === 'user' && excludedUserIDs.has(itemId);
      const isExcludedGroup = item.type === 'group' && excludedGroupIDs.has(itemId);
      const isParticipant = this.participants.some((participant) =>
        Number(participant.id) === itemId && participant.type === item.type
      );

      return !isParticipant && !isExcludedUser && !isExcludedGroup;
    })];
  }

  public addUserGroup(userOrGroup: string | number | TYPE.FieldSettingsModelWithId): void {
    if (!this.canEditUserGroupSelection()) {
      return;
    }

    if (typeof userOrGroup === 'string' || typeof userOrGroup === 'number') {
      userOrGroup = this.userGroupOptions.find((item) => item.id === userOrGroup) as TYPE.FieldSettingsModelWithId;
    }
    if (this.participants.find((item) => item.id === userOrGroup.id && item.type === userOrGroup.type)) {
      // already added
    } else {
      userOrGroup.push = this.getNewPushState();
      this.participants.push(userOrGroup);
      this.userGroupMailForm.addControl(userOrGroup.type! + userOrGroup.id, this.formBuilder.control(false));
    }
    setTimeout(() => {
      if (!this.singleSelectionStays()) {
        this.userGroupMailForm.controls['userGroup'].setValue(null);
        this.removeExistingParticipants();
      }
      this.updateEmitters();
      // Trigger change detection for OnPush parent components
      this.cdr.markForCheck();
    }, 500);
  }

  public addMail(): void {
    if (!this.canEditExternalEmails()) {
      return;
    }

    const mail = this.userGroupMailForm.controls['addMail'].value;

    if (mail && this.userGroupMailForm.controls['addMail'].valid && !this.participants.find((item) => item.id === mail && item.type === 'mail')) {
      this.participants.push({
        id: mail,
        name: mail,
        type: 'mail',
        push: this.getNewPushState()
      });

      this.userGroupMailForm.addControl('mail' + mail, this.formBuilder.control(false));
      this.userGroupMailForm.controls['addMail'].setValue('');
      this.updateEmitters();
    }
  }

  public selectAll(): void {
    this.participants.forEach((item) => {
      if (this.canEditParticipant(item)) {
        this.userGroupMailForm.controls[item.type! + item.id].setValue(true);
      }
    });
  }

  public deselectAll(): void {
        this.participants.forEach((item) => {
      if (this.canEditParticipant(item)) {
        this.userGroupMailForm.controls[item.type! + item.id].setValue(false);
      }
    });
  }

  public allSelected(): boolean {
    const editableParticipants = this.participants.filter((item) => this.canEditParticipant(item));
    return editableParticipants.length > 0 && editableParticipants.every((item) => {
      return this.userGroupMailForm.controls[item.type! + item.id].value;
    })
  }

  public removeSelected(): void {
    this.participants.forEach((item) => {
      if (this.canEditParticipant(item) && this.userGroupMailForm.controls[item.type! + item.id].value) {
        this.userGroupMailForm.removeControl(item.type! + item.id);
      }
    });
    this.participants = this.participants.filter((item) => this.userGroupMailForm.controls[item.type! + item.id]);
    if (this.usesLegacyIdLimitedMode()) {
      this.refreshAvailableOptions();
    }
    this.updateEmitters();
  }

  public showDeleteOverlay(item: TYPE.FieldSettingsModelWithId): void {
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('CONFIRMITEMDELETE') + '</h6>',
      content: this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE'),
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.languageService.getTranslationByKey('DELETE'),
        click: () => this.showDeleteOverlayConfirmed(item),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  public resolveGroupToUsers(groupParticipant: TYPE.FieldSettingsModelWithId): void {
    if (!this.canEditParticipant(groupParticipant)) {
      return;
    }

    if (groupParticipant.type !== 'group' || typeof groupParticipant.id !== 'number') {
      return;
    }

    const groupControlName = 'group' + groupParticipant.id;
    const subscription = this.apiService.getUsersByGroupId(groupParticipant.id).subscribe({
      next: (users: UserMinimalResource[]) => {
        const existingUserIds = new Set(
          this.participants
            .filter((participant) => participant.type === 'user')
            .map((participant) => participant.id as number)
        );

        const resolvedUsers = users
          .filter((user) => !existingUserIds.has(user.id))
          .map((user) => ({
            id: user.id,
            image: user.avatar?.path ?? '',
            name: `${user.last_name}, ${user.first_name}`,
            type: 'user' as const,
            push: groupParticipant.push ?? this.getNewPushState(),
            admin: this.adminUserIDs().includes(user.id)
          }));

        this.userGroupMailForm.removeControl(groupControlName);
        this.participants = this.participants.filter((participant) =>
          participant.id !== groupParticipant.id || participant.type !== groupParticipant.type
        );

        resolvedUsers.forEach((user) => {
          this.participants.push(user);
          this.userGroupMailForm.addControl(user.type + user.id, this.formBuilder.control(false));
        });

        if (this.usesLegacyIdLimitedMode()) {
          this.refreshAvailableOptions();
        } else {
          this.addParticipantBackToOptions(groupParticipant);
          this.removeExistingParticipants();
        }

        this.userGroupMailForm.controls['userGroup'].setValue(null);
        this.updateEmitters();
        this.cdr.markForCheck();
      }
    });

    this.subscriptions.push(subscription);
  }

  private showDeleteOverlayConfirmed(item: TYPE.FieldSettingsModelWithId): void {
    this.userGroupMailForm.removeControl(item.type! + item.id);
    this.participants = this.participants.filter((participant) => participant.id !== item.id || participant.type !== item.type);
    this.dialogInstance?.hide();
    
    if (this.usesLegacyIdLimitedMode()) {
      this.refreshAvailableOptions();
    } else {
      if (item.type !== 'mail' && !this.singleSelectionStays()) {
        this.addParticipantBackToOptions(item);
      }
    }
    
    this.userGroupMailForm.controls['userGroup'].setValue(null);
    this.updateEmitters();
    // Trigger change detection for OnPush parent components
    this.cdr.markForCheck();
  }

  private sortUserGroupOptions(
    items: TYPE.FieldSettingsModelWithId[]
  ): TYPE.FieldSettingsModelWithId[] {
    return [...items].sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'group' ? -1 : 1;
      }

      return (a.name ?? '').localeCompare(b.name ?? '');
    });
  }

  private addParticipantBackToOptions(item: TYPE.FieldSettingsModelWithId): void {
    if (item.type === 'mail' || this.singleSelectionStays()) {
      return;
    }

    if (this.userGroupOptions.some((option) => option.id === item.id && option.type === item.type)) {
      return;
    }

    this.userGroupOptions = this.sortUserGroupOptions([
      ...this.userGroupOptions,
      item
    ]);
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }

  private loadInitials(): void {
    const loadVersion = ++this.initialsLoadVersion;
    const users$ = this.user().length
      ? this.apiService.getUsersByIds(this.user())
      : of([]);

    const groups$ = this.groups().length
      ? this.apiService.getGroupsByIds(this.groups())
      : of([]);

    this.subscriptions.push(forkJoin({users: users$, groups: groups$}).subscribe(({users, groups}) => {
      if (loadVersion !== this.initialsLoadVersion) {
        return;
      }

      const requestedUserIDs = new Set(this.user());
      const requestedGroupIDs = new Set(this.groups());
      const nextParticipants: TYPE.FieldSettingsModelWithId[] = [];

      (users as UserMinimalResource[])
        .filter((user) => requestedUserIDs.has(user.id))
        .forEach((user) => {
          nextParticipants.push({
            id: user.id,
            image: user.avatar?.path ?? '',
            name: user.last_name + ', ' + user.first_name,
            type: 'user',
            push: this.getLoadedPushState({id: user.id, type: 'user'}),
            admin: this.adminUserIDs().includes(user.id)
          });
        });

      (groups as GroupMinimalResource[])
        .filter((group) => requestedGroupIDs.has(group.id))
        .forEach((group) => {
          nextParticipants.push({
            id: group.id,
            description: group.description,
            count: group.user_count,
            image: group.avatar?.path ?? '',
            name: group.name,
            type: 'group',
            push: this.getLoadedPushState({id: group.id, type: 'group'})
          });
        });
      //though push: push: this.getLoadedPushState({id: mail, type: 'mail'}) is not implemented on the backend side for consistency I will still set it here for future use
      this.mails().forEach((mail) => {
        nextParticipants.push({
          id: mail,
          name: mail,
          type: 'mail',
          push: this.getLoadedPushState({id: mail, type: 'mail'})
        });
      });

      this.syncParticipantControls(nextParticipants);
      this.participants = nextParticipants;
      this.removeExistingParticipants();
      this.emitModelChanges();
      this.cdr.markForCheck();
    }));
  }

  private hasDifferentParticipantsFromInputs(): boolean {
    const participantUsers = this.participants
      .filter((item) => item.type === 'user')
      .map((item) => item.id as number);
    const participantGroups = this.participants
      .filter((item) => item.type === 'group')
      .map((item) => item.id as number);
    const participantMails = this.participants
      .filter((item) => item.type === 'mail')
      .map((item) => item.id as string);
    const participantAdminUsers = this.participants
      .filter((item) => item.type === 'user' && item.admin)
      .map((item) => item.id as number);
    const participantPushUsers = this.participants
      .filter((item) => item.type === 'user' && item.push)
      .map((item) => item.id as number);
    const participantPushGroups = this.participants
      .filter((item) => item.type === 'group' && item.push)
      .map((item) => item.id as number);

    return this.getComparableKey(participantUsers) !== this.getComparableKey(this.user())
      || this.getComparableKey(participantGroups) !== this.getComparableKey(this.groups())
      || this.getComparableKey(participantMails) !== this.getComparableKey(this.mails())
      || this.getComparableKey(participantAdminUsers) !== this.getComparableKey(this.adminUserIDs())
      || this.getComparableKey(participantPushUsers) !== this.getComparableKey(this.pushUserIDs())
      || this.getComparableKey(participantPushGroups) !== this.getComparableKey(this.pushGroupIDs());
  }

  private syncParticipantControls(participants: TYPE.FieldSettingsModelWithId[]): void {
    Object.keys(this.userGroupMailForm.controls)
      .filter((controlName) => controlName !== 'userGroup' && controlName !== 'addMail' && controlName !== 'userGroupMailFilter')
      .forEach((controlName) => this.userGroupMailForm.removeControl(controlName));

    participants.forEach((participant) => {
      this.userGroupMailForm.addControl(participant.type! + participant.id, this.formBuilder.control(false));
    });
  }

  private getComparableKey(values: (string | number)[]): string {
    return [...values]
      .map((value) => String(value))
      .sort()
      .join('|');
  }

  private updateEmitters(): void {
    this.userChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => item.id as number));
    this.groupsChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => item.id as number));
    this.mailsChange.emit(this.participants.filter((item) => item.type === 'mail').map((item) => item.id as string));
    this.emitPushChanges();
    this.adminUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.admin).map((item) => item.id as number));
    this.adminGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.admin).map((item) => item.id as number));
    this.userNamesChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => item.name as string));
    this.groupNamesChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => item.name as string));
    this.emitModelChanges();
  }

  public togglePush(participant: TYPE.FieldSettingsModelWithId): void {
    if (!this.canEditParticipant(participant)) {
      return;
    }

    participant.push = !participant.push;
    this.emitPushChanges();
    this.emitModelChanges();
  }

  public hasPushParticipants(): boolean {
    return this.participants.some((item) =>
      item.type === 'user' || item.type === 'group' || item.type === 'mail'
    );
  }

  public allPushEnabled(): boolean {
    const pushParticipants = this.participants.filter((item) => item.type === 'user' || item.type === 'group');
    return pushParticipants.length > 0 && pushParticipants.every((item) => item.push);
  }

  public enablePushForAll(): void {
    if (!this.hasEditableParticipants()) {
      return;
    }

    this.setPushForAll(true);
  }

  public disablePushForAll(): void {
    if (!this.hasEditableParticipants()) {
      return;
    }

    this.setPushForAll(false);
  }

  private getLoadedPushState(
    participant: Pick<TYPE.FieldSettingsModelWithId, 'id' | 'type'>
  ): boolean | null {
    if (!this.allowPush()) {
      return null;
    }

    if (participant.type === 'user') {
      return this.pushUserIDs().includes(participant.id as number);
    }

    if (participant.type === 'group') {
      return this.pushGroupIDs().includes(participant.id as number);
    }

    if (participant.type === 'mail') {
      return null;
    }

    return null;
  }

  private getNewPushState(): boolean | null {
    if (!this.allowPush()) {
      return null;
    }

    return this.defaultPushState();
  }

  private emitPushChanges(): void {
    this.pushUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.push).map((item) => item.id as number));
    this.pushGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.push).map((item) => item.id as number));
  }

  private setPushForAll(value: boolean): void {
    this.participants.forEach((participant) => {
      if (participant.type === 'user' || participant.type === 'group' || participant.type === 'mail') {
        participant.push = value;
      }
    });

    this.emitPushChanges();
    this.emitModelChanges();
    this.cdr.markForCheck();
  }

  public toggleAdmin(participant: TYPE.FieldSettingsModelWithId): void {
    if (!this.canEditParticipant(participant)) {
      return;
    }

    participant.admin = !participant.admin;
    this.adminUserIDsChange.emit(this.participants.filter((item) => item.type === 'user' && item.admin).map((item) => item.id as number));
    this.adminGroupIDsChange.emit(this.participants.filter((item) => item.type === 'group' && item.admin).map((item) => item.id as number));
    this.emitModelChanges();
  }

  private emitModelChanges(): void {
    this.userModelChange.emit(this.participants.filter((item) => item.type === 'user').map((item) => this.mapFieldSettingsToUserMinimalResource(item)));
    this.groupModelChange.emit(this.participants.filter((item) => item.type === 'group').map((item) => this.mapFieldSettingsToGroupMinimalResource(item)));
  }

  public mapFieldSettingsToUserMinimalResource(field: TYPE.FieldSettingsModelWithId): UserMinimalResource {
    const [lastName = '', firstName = ''] = (field.name ?? '').split(',').map((namePart) => namePart.trim());

    return {
      id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
      avatar: this.mapImagePathToAvatar(field.image),
      first_name: firstName,
      last_name: lastName
    };
  }

  public mapFieldSettingsToGroupMinimalResource(field: TYPE.FieldSettingsModelWithId): GroupMinimalResource {
    return {
      id: typeof field.id === 'string' ? parseInt(field.id, 10) : field.id,
      name: field.name ?? '',
      description: field.description ?? null,
      avatar: this.mapImagePathToAvatar(field.image),
      user_count: field.count ?? undefined
    };
  }

  private mapImagePathToAvatar(imagePath: string | null | undefined): UserMinimalResource['avatar'] {
    if (!imagePath) {
      return null;
    }

    return {path: imagePath} as UserMinimalResource['avatar'];
  }
}
